import { useId, useState } from 'react';
import type { Problem, StageCondition } from '../api/types';
import { Button } from '../components/Button';
import { stageMoveProblemText } from '../app/status';

/** 6 段階（設計 01 章）。完了＝緑、現在＝橙、未着手＝灰。戻ることがある。 */
export const STAGE_LABELS = ['イメージ確定', '業務フロー', '実行可能化', '実装', '試運転', '公開'] as const;

export interface StageBarProps {
  stage: number | null;
  /** 現在の段階から次へ進む条件（GET A/draft/review の conditions）。 */
  conditions?: StageCondition[];
  /** 移動の要求中。 */
  busy?: boolean;
  /** 移動の失敗（ポップオーバーの中に出す）。 */
  problem?: Problem | null;
  /** セッションが waiting_answer（U6）。現在の段階にバッジを出す。 */
  waitingAnswer?: boolean;
  /** 無ければ S3 の表示だけ（fixture モード、Draft が無いとき）。 */
  onAdvance?(): void;
  /** 戻る理由は前後の空白を落として渡す（1 字以上）。 */
  onBack?(reason: string): void;
}

/**
 * Draft の段階（1〜6）。onAdvance を渡すと現在の段階を押せ、次へ進める条件と移動のポップオーバーを開く（D12〜D14）。
 * 完了は step < stage のまま（戻っても最高到達段階は持たない。D29）。
 */
export function StageBar({ stage, conditions = [], busy = false, problem = null, waitingAnswer = false, onAdvance, onBack }: StageBarProps) {
  const popoverId = useId();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [shownStage, setShownStage] = useState(stage);
  if (shownStage !== stage) {
    // 段階が動いたら（進めた・戻した・変更案を反映した）ポップオーバーを閉じ、書きかけの理由を捨てる。
    setShownStage(stage);
    setOpen(false);
    setReason('');
  }
  if (stage === null) return null;

  const interactive = onAdvance !== undefined;
  const list = (
    <ol className="studio-stagebar" aria-label="段階">
      {STAGE_LABELS.map((label, index) => {
        const step = index + 1;
        const state = step < stage ? 'done' : step === stage ? 'current' : 'todo';
        return (
          <li
            key={label}
            className={`studio-stage-step studio-stage-step-${state}`}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            {interactive && state === 'current' ? (
              <button
                type="button"
                className="studio-stage-button"
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls={popoverId}
                onClick={() => setOpen((value) => !value)}
              >
                {label}
              </button>
            ) : (
              label
            )}
            {state === 'current' && waitingAnswer && <span className="studio-stage-waiting">回答待ち</span>}
          </li>
        );
      })}
    </ol>
  );
  if (!interactive) return list;

  const last = stage >= STAGE_LABELS.length;
  // 条件をまだ読んでいない（0 件）ときも進めない。段階 1〜5 には必ず 1 つ以上の条件がある。
  const blocked = last || conditions.length === 0 || conditions.some((condition) => !condition.met);

  return (
    <div className="studio-stagebar-wrap">
      {list}
      {open && (
        <div id={popoverId} className="studio-stage-popover" role="dialog" aria-label="段階の移動">
          <div className="row">
            <h3 className="studio-stage-popover-title">{`現在の段階 · ${STAGE_LABELS[stage - 1] ?? ''}`}</h3>
            <span className="spacer" />
            <Button variant="text" onClick={() => setOpen(false)}>
              閉じる
            </Button>
          </div>
          <h4 className="studio-stage-popover-label">次へ進める条件</h4>
          {last ? (
            <p className="studio-stage-condition-detail">最後の段階です</p>
          ) : (
            <ul className="studio-stage-conditions" aria-label="次へ進める条件">
              {conditions.map((condition) => (
                <li
                  key={condition.code}
                  className={condition.met ? 'studio-stage-condition studio-stage-condition-met' : 'studio-stage-condition'}
                >
                  <span className="studio-stage-mark">{condition.met ? '✓' : '○'}</span>
                  <span>{condition.label}</span>
                  {condition.detail && <span className="studio-stage-condition-detail">{condition.detail}</span>}
                </li>
              ))}
            </ul>
          )}
          <textarea
            className="studio-stage-reason"
            aria-label="戻る理由"
            placeholder="戻るときは理由を書いてください（AI にも伝わります）"
            value={reason}
            rows={2}
            maxLength={500}
            disabled={busy || stage <= 1}
            onChange={(event) => setReason(event.target.value)}
          />
          {problem !== null && (
            <p className="form-conflict" role="alert">
              {stageMoveProblemText(problem) ?? problem.detail}
            </p>
          )}
          <div className="row">
            <Button disabled={busy || stage <= 1 || reason.trim() === ''} onClick={() => onBack?.(reason.trim())}>
              前の段階に戻る
            </Button>
            <span className="spacer" />
            {/* interactive は別名の変数なので、クロージャの中では onAdvance の undefined が外れない。 */}
            <Button variant="primary" disabled={busy || blocked} onClick={() => onAdvance?.()}>
              次へ進める
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
